import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import type { TextInput } from "react-native";
import useSendSMSCode from "../hooks/useSMSCodeSend";

import { getPhoneCountryCode, phoneFormSchema, phoneWithCountryCode } from "@stepapp/core";
import { Block, Button, PhoneInput, Typography } from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { AppStorage } from "../services/AppStorage";
import LoginModal from "./Modal/LoginModal";

type FormValues = {
  phoneNumber: string;
};

const LoginForm = forwardRef<TextInput>((_, ref) => {
  const { t } = useTranslation();
  const sendSMSCode = useSendSMSCode();
  const countryCode = AppStorage().get("countryCode");

  const [loginModal, setLoginModal] = useState({
    phoneNumber: "",
    type: "",
  });

  const onSubmit = useCallback(
    async ({ phoneNumber }: FormValues) => {
      const { success, isRegister, error } = await sendSMSCode({
        phoneNumber: phoneWithCountryCode(phoneNumber, countryCode),
      });

      if (success) {
        setLoginModal({ phoneNumber, type: isRegister ? "register" : "login" });
      } else {
        alert(error || t("common.error"));
      }
    },
    [sendSMSCode, countryCode, t],
  );

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(phoneFormSchema(t, countryCode)),
    defaultValues: { phoneNumber: "" },
  });

  const phoneNumber = watch("phoneNumber");

  const toggleLoginModal = useCallback(() => {
    setLoginModal({ phoneNumber: "", type: "" });
  }, []);

  return (
    <>
      <Typography type="primary" variant="newH3">
        {t("screens.serviceSummary.loginToViewDetails")}
      </Typography>

      <Block marginTop={2} marginBottom={3}>
        <Typography type="secondary" variant="lightLabel">
          {t("pages.serviceSummary.SMSInfo")}
        </Typography>
      </Block>

      <PhoneInput
        error={errors?.phoneNumber?.message}
        label={t("common.phone")}
        onBlur={() => trigger("phoneNumber")}
        onChangeText={(value: string) =>
          setValue("phoneNumber", value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: phoneNumber?.length >= 8,
          })
        }
        prefix={getPhoneCountryCode(countryCode)}
        ref={ref}
        value={phoneNumber}
      />
      <Button
        disabled={!isValid}
        label={t("screens.serviceSummary.sendSMSCode")}
        onPress={handleSubmit(onSubmit)}
        type="primary"
      />

      <LoginModal
        loginType={loginModal.type}
        phoneNumber={loginModal.phoneNumber}
        toggle={toggleLoginModal}
      />

      {/* // TODO uncomment once we implement google login */}
      {/* <Block marginVertical={1}>
        <Divider separator={t("common.or").toLowerCase()} />
      </Block>
      <GoogleButton /> */}
    </>
  );
});

export default LoginForm;
